import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, Tooltip, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { motion, useMotionValue, useSpring, useTransform, useMotionValueEvent, useScroll } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LAYOUTS, LineLayout, Pt, mirrorLayout, smoothPath } from './geometry';
import { useAppTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

const STROKE = 7;
const BEATS = ['understand', 'talk', 'grow', 'play', 'meet'] as const;
const BEAT_LINKS = ['/learn', '/consultations', '/activities', '/activities', '/signup'];

/* ---------- helpers ---------- */

/** For a path, the stroke length reached once the pen has passed a given y (monotone envelope). */
const buildEnvelope = (el: SVGPathElement, H: number) => {
  const total = el.getTotalLength();
  const bins = 400;
  const env = new Array<number>(bins + 1).fill(0);
  const N = 1200;
  for (let k = 0; k <= N; k++) {
    const L = (k / N) * total;
    const p = el.getPointAtLength(L);
    const b = Math.min(bins, Math.max(0, Math.floor((p.y / H) * bins)));
    env[b] = Math.max(env[b], L);
  }
  for (let b = 1; b <= bins; b++) env[b] = Math.max(env[b], env[b - 1]);
  const at = (yy: number) => {
    const f = Math.min(bins, Math.max(0, (yy / H) * bins));
    const i = Math.floor(f);
    const j = Math.min(bins, i + 1);
    return env[i] + (env[j] - env[i]) * (f - i);
  };
  const lengthNear = (pt: Pt) => {
    let best = 0, bestD = Infinity;
    for (let k = 0; k <= N; k += 2) {
      const L = (k / N) * total;
      const p = el.getPointAtLength(L);
      const d = (p.x - pt[0]) ** 2 + (p.y - pt[1]) ** 2;
      if (d < bestD) { bestD = d; best = L; }
    }
    return best;
  };
  return { total, at, lengthNear };
};

const pct = (v: number, of: number) => `${(v / of) * 100}%`;

/* ---------- component ---------- */

const TwoLines: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const { reduceMotion } = useAppTheme();
  const { isRtl } = useLanguage();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const base: LineLayout = isMobile ? LAYOUTS.mobile : LAYOUTS.desktop;
  const layout = useMemo(() => (isRtl ? mirrorLayout(base) : base), [base, isRtl]);
  const { W, H, anchors } = layout;

  const childD = useMemo(() => smoothPath(layout.child), [layout]);
  const parentD = useMemo(() => smoothPath(layout.parent), [layout]);
  const childColor = dark ? theme.palette.brand.lavender : '#8F84FF';
  const parentColor = dark ? '#3D8FE0' : theme.palette.brand.navy;

  const canvasRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<SVGPathElement>(null);
  const parentRef = useRef<SVGPathElement>(null);
  const childTip = useRef<SVGCircleElement>(null);
  const parentTip = useRef<SVGCircleElement>(null);
  const envRef = useRef<{ c: ReturnType<typeof buildEnvelope>; p: ReturnType<typeof buildEnvelope> } | null>(null);

  const [totals, setTotals] = useState({ c: 1, p: 1 });
  const [marks, setMarks] = useState({ bubbleEnd: 0, smileEnd: 0, milestones: [0, 0, 0], quote: 0 });
  const [scale, setScale] = useState(1);
  const [remeasure, setRemeasure] = useState(0);

  const childLen = useMotionValue(0);
  const parentLen = useMotionValue(0);
  const childDrawn = useSpring(childLen, { stiffness: 90, damping: 26, mass: 0.5 });
  const parentDrawn = useSpring(parentLen, { stiffness: 90, damping: 26, mass: 0.5 });
  const childOffset = useTransform(childDrawn, (v) => totals.c - v);
  const parentOffset = useTransform(parentDrawn, (v) => totals.p - v);

  // measure paths whenever the layout changes
  useLayoutEffect(() => {
    const c = childRef.current, p = parentRef.current;
    if (!c || !p) return;
    const ce = buildEnvelope(c, H), pe = buildEnvelope(p, H);
    envRef.current = { c: ce, p: pe };
    setTotals({ c: ce.total, p: pe.total });
    setMarks({
      bubbleEnd: ce.lengthNear(anchors.bubbleExit),
      smileEnd: ce.lengthNear(anchors.smileEnd),
      milestones: anchors.milestones.map((m) => ce.lengthNear(m)),
      quote: pe.lengthNear(anchors.curlExit),
    });
    const measure = () => { if (canvasRef.current) setScale(canvasRef.current.clientWidth / W); };
    measure();
    window.requestAnimationFrame(() => setRemeasure((n) => n + 1));
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [layout, H, W, anchors]);

  // scroll → how far each pen has travelled
  const { scrollY } = useScroll();
  const update = useCallback(() => {
    const env = envRef.current, el = canvasRef.current;
    if (!env || !el) return;
    if (reduceMotion) { childLen.set(env.c.total); parentLen.set(env.p.total); return; }
    const top = el.getBoundingClientRect().top + window.scrollY;
    const scale = el.clientWidth / W;
    const yPx = window.scrollY + window.innerHeight * 0.62 - top;
    // at the very top the pens rest on their dots; the lead fades in over the first 600px
    const restPx = layout.child[0][1] * scale;
    const lead = Math.max(0, yPx - restPx) * Math.max(0, 1 - window.scrollY / 600);
    let yU = (yPx - lead) / scale;
    // final approach: once the pen is near the mark, finish quickly so the logo completes
    // while it is still in the upper half of the screen, with the text and button below it
    const { from, to } = anchors.finish;
    if (yU > from) yU = from + (yU - from) * ((H - from) / (to - from));
    childLen.set(env.c.at(yU));
    parentLen.set(env.p.at(yU));
  }, [childLen, parentLen, reduceMotion, W, H, layout, anchors]);
  useMotionValueEvent(scrollY, 'change', update);
  useEffect(() => { update(); const id = window.setTimeout(update, 300); return () => window.clearTimeout(id); }, [update, totals, remeasure]);

  // pen tips follow the drawn length
  useMotionValueEvent(childDrawn, 'change', (v) => {
    const el = childRef.current, tip = childTip.current;
    if (!el || !tip) return;
    const p = el.getPointAtLength(Math.max(0, Math.min(v, totals.c)));
    tip.setAttribute('cx', String(p.x)); tip.setAttribute('cy', String(p.y));
  });
  useMotionValueEvent(parentDrawn, 'change', (v) => {
    const el = parentRef.current, tip = parentTip.current;
    if (!el || !tip) return;
    const p = el.getPointAtLength(Math.max(0, Math.min(v, totals.p)));
    tip.setAttribute('cx', String(p.x)); tip.setAttribute('cy', String(p.y));
  });

  // in the hero, the two dots lean a little toward the cursor
  const leanX = useSpring(useMotionValue(0), { stiffness: 80, damping: 18 });
  const leanY = useSpring(useMotionValue(0), { stiffness: 80, damping: 18 });
  useEffect(() => {
    if (reduceMotion) return;
    const onMove = (e: MouseEvent) => {
      if (window.scrollY > window.innerHeight * 0.6) { leanX.set(0); leanY.set(0); return; }
      leanX.set((e.clientX / window.innerWidth - 0.5) * 28);
      leanY.set((e.clientY / window.innerHeight - 0.5) * 28);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [leanX, leanY, reduceMotion]);
  const leanFade = useTransform(scrollY, [0, 220], [1, 0]);
  const leanTx = useTransform([leanX, leanFade], ([x, f]: number[]) => x * f);
  const leanTy = useTransform([leanY, leanFade], ([y, f]: number[]) => y * f);

  // reveal thresholds derived from drawn lengths
  const quoteOpacity = useTransform(parentDrawn, [marks.quote - 40, marks.quote + 20], [0, 1]);
  const bubbleOpacity = useTransform(childDrawn, [marks.bubbleEnd - 120, marks.bubbleEnd - 20], [0, 1]);
  const eyesScale = useTransform(childDrawn, [marks.smileEnd - 10, marks.smileEnd + 30], [0, 1]);
  const meetProgress = useTransform([childDrawn, parentDrawn], ([c, p]: number[]) => Math.min((c - (totals.c - 60)) / 60, (p - (totals.p - 60)) / 60));
  const markOpacity = useTransform(meetProgress, [0, 1], [0, 1]);
  const tipOpacity = useTransform(meetProgress, [0.2, 0.9], [1, 0]);
  const lettersOpacity = useTransform(meetProgress, [0.45, 1], [0, 1]);
  const lettersX = useTransform(meetProgress, [0.45, 1], [36, 0]);

  const [wink, setWink] = useState(false);
  const [typed, setTyped] = useState('');
  const bubbleText = t('home.beats.talk.bubble') as string;
  const [bubbleOn, setBubbleOn] = useState(false);
  useMotionValueEvent(bubbleOpacity, 'change', (v) => { if (v > 0.9) setBubbleOn(true); });
  useEffect(() => {
    if (!bubbleOn) return;
    if (reduceMotion) { setTyped(bubbleText); return; }
    let i = 0;
    const id = window.setInterval(() => { i += 1; setTyped(bubbleText.slice(0, i)); if (i >= bubbleText.length) window.clearInterval(id); }, 34);
    return () => window.clearInterval(id);
  }, [bubbleOn, bubbleText, reduceMotion]);

  const fs = (u: number) => `${u * scale}px`;

  return (
    <Box
      ref={canvasRef}
      sx={{ position: 'relative', width: '100%', aspectRatio: `${W} / ${H}`, mt: { xs: 9, md: 6 } }}
    >
      {/* ---------------- the two strokes ---------------- */}
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        aria-hidden
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}
      >
        {/* faint guide of the full journey */}
        <path d={childD} fill="none" stroke={childColor} strokeWidth={STROKE} strokeLinecap="round" opacity={dark ? 0.08 : 0.06} />
        <path d={parentD} fill="none" stroke={parentColor} strokeWidth={STROKE} strokeLinecap="round" opacity={dark ? 0.08 : 0.05} />
        <motion.path ref={childRef} d={childD} fill="none" stroke={childColor} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray={totals.c} style={{ strokeDashoffset: childOffset }} />
        <motion.path ref={parentRef} d={parentD} fill="none" stroke={parentColor} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray={totals.p} style={{ strokeDashoffset: parentOffset }} />
        <motion.g style={{ x: leanTx, y: leanTy, opacity: tipOpacity }}>
          <circle ref={childTip} cx={layout.child[0][0]} cy={layout.child[0][1]} r={isMobile ? 22 : 15} fill={childColor} />
          <circle ref={parentTip} cx={layout.parent[0][0]} cy={layout.parent[0][1]} r={isMobile ? 30 : 21} fill={parentColor} />
        </motion.g>
        {/* eyes above the smile */}
        <motion.g style={{ scale: eyesScale, transformOrigin: `${(anchors.eyes[0][0] + anchors.eyes[1][0]) / 2}px ${anchors.eyes[0][1]}px` }}>
          <circle cx={anchors.eyes[0][0]} cy={anchors.eyes[0][1]} r={isMobile ? 18 : 12} fill={childColor} />
          {wink ? (
            <path d={`M ${anchors.eyes[1][0] - (isMobile ? 22 : 15)} ${anchors.eyes[1][1]} q ${isMobile ? 22 : 15} ${isMobile ? -18 : -12} ${isMobile ? 44 : 30} 0`} fill="none" stroke={childColor} strokeWidth={STROKE} strokeLinecap="round" />
          ) : (
            <circle cx={anchors.eyes[1][0]} cy={anchors.eyes[1][1]} r={isMobile ? 18 : 12} fill={childColor} />
          )}
        </motion.g>
        {/* milestone dots */}
        {anchors.milestones.map((m, i) => (
          <MilestoneDot key={i} pt={m} at={marks.milestones[i]} drawn={childDrawn} color={parentColor} r={isMobile ? 16 : 11} />
        ))}
      </Box>

      {/* ---------------- hero text ---------------- */}
      <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: { xs: 'calc(100vh - 72px)', md: 'calc(100vh - 48px)' }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, textAlign: 'center' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: isRtl ? 3.5 : 2.5 }}>{t('home.hero.eyebrow')}</Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.6rem', md: '4.6rem' }, maxWidth: 900, mx: 'auto', letterSpacing: isRtl ? 0 : '-0.025em', lineHeight: isRtl ? 1.35 : 1.08 }}>
            {t('home.hero.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mt: 3, fontSize: { xs: '1.05rem', md: '1.2rem' } }}>
            {t('home.hero.subtitle')}
          </Typography>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} style={{ marginTop: 36 }}>
          <Typography variant="overline" color="text.secondary">{t('home.hero.scroll')}</Typography>
          <motion.div animate={reduceMotion ? undefined : { y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }} style={{ width: 2, height: 36, margin: '8px auto 0', background: alpha(theme.palette.text.primary, 0.3), borderRadius: 2 }} />
        </motion.div>
      </Box>

      {/* ---------------- beat texts ---------------- */}
      {BEATS.map((key, i) => {
        const a = anchors.beats[i].text;
        const isMeet = key === 'meet';
        return (
          <Box
            key={key}
            component={motion.div}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
            style={{ position: 'absolute', left: pct(a.x, W), top: pct(a.y, H), width: pct(a.w, W) }}
            sx={{ textAlign: a.align }}
          >
            <Typography variant="overline" sx={{ color: i % 2 ? parentColor : childColor, display: 'block', mb: 1.5 }}>
              {t(`home.beats.${key}.eyebrow`)}
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: isMeet ? '3rem' : '2.6rem' }, letterSpacing: isRtl ? 0 : '-0.02em', lineHeight: isRtl ? 1.4 : 1.1, mb: 2 }}>
              {t(`home.beats.${key}.title`)}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, mb: 2.5 }}>
              {t(`home.beats.${key}.text`)}
            </Typography>
            {!isMeet && (
              <Button component={RouterLink} to={BEAT_LINKS[i]} variant="text" sx={{ px: 0, fontWeight: 800, color: 'text.primary', '&:hover': { bgcolor: 'transparent', color: 'primary.main' } }}>
                {t(`home.beats.${key}.link`)} {isRtl ? '←' : '→'}
              </Button>
            )}
          </Box>
        );
      })}

      {/* beat 1: the question, next to the curl */}
      <Box component={motion.div} style={{ opacity: quoteOpacity, position: 'absolute', left: pct(anchors.quote[0] - 150, W), top: pct(anchors.quote[1], H), width: pct(300, W) }}
        sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.35rem' }, fontWeight: 700, fontStyle: 'italic', color: parentColor }}>
          {t('home.beats.understand.quote')}
        </Typography>
      </Box>

      {/* beat 2: inside the bubble */}
      <Box component={motion.div} style={{ opacity: bubbleOpacity, position: 'absolute', left: pct(anchors.bubble.x, W), top: pct(anchors.bubble.y, H), width: pct(anchors.bubble.w, W), height: pct(anchors.bubble.h, H) }}
        sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: { xs: '1rem', md: '1.05rem' }, fontWeight: 700, lineHeight: 1.45 }} aria-live="polite">
          {typed}
          {bubbleOn && typed.length < bubbleText.length && <span style={{ opacity: 0.5 }}>▍</span>}
        </Typography>
      </Box>

      {/* beat 3: milestone labels */}
      {anchors.milestones.map((m, i) => (
        <MilestoneLabel key={i} pt={m} at={marks.milestones[i]} drawn={childDrawn} label={t(`home.beats.grow.milestones.${i}`)} date={t(`home.beats.grow.dates.${i}`)} W={W} H={H} />
      ))}

      {/* beat 4: tap the smile */}
      <Box
        component="button"
        type="button"
        aria-label={t('home.beats.play.hint')}
        aria-pressed={wink}
        onClick={() => setWink((w) => !w)}
        style={{
          position: 'absolute', left: pct(anchors.eyes[0][0] - 90, W), top: pct(anchors.eyes[0][1] - 60, H),
          width: pct(anchors.eyes[1][0] - anchors.eyes[0][0] + 180, W), height: pct(240, H),
        }}
        sx={{ background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '50%' }}
      />
      <Box component={motion.div} style={{ opacity: eyesScale, position: 'absolute', left: pct(anchors.eyes[0][0] - 60, W), top: pct(anchors.smileEnd[1] + 150, H), width: pct(anchors.eyes[1][0] - anchors.eyes[0][0] + 120, W) }}
        sx={{ textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">{wink ? t('home.beats.play.winked') : t('home.beats.play.hint')}</Typography>
      </Box>

      {/* beat 5: the meeting becomes the logo */}
      <motion.img src="/brand/logo-drop-crop.png" alt="" style={{ opacity: markOpacity, position: 'absolute', left: pct(anchors.meet.x, W), top: pct(anchors.meet.y, H), width: pct(anchors.meet.w, W), pointerEvents: 'none' }} />
      <motion.img src="/brand/logo-letters-crop.png" alt="نديم" style={{ opacity: lettersOpacity, x: lettersX, position: 'absolute', left: pct(anchors.meet.x - anchors.meet.w * 4.87, W), top: pct(anchors.meet.y + anchors.meet.w * 0.86, H), width: pct(anchors.meet.w * 4.94, W), pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', left: 0, right: 0, top: pct(anchors.cta.y, H), textAlign: 'center', px: 3 }}>
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
            <Button component={RouterLink} to="/signup" variant="gradient" size="large" sx={{ px: 4, fontSize: fs(isMobile ? 40 : 17) }}>
              {t('home.beats.meet.cta')}
            </Button>
            <Button component={RouterLink} to="/login" variant="text" sx={{ color: 'text.secondary' }}>{t('home.beats.meet.secondary')}</Button>
          </Stack>
        </motion.div>
      </Box>
    </Box>
  );
};

/* ---------- small pieces ---------- */

const MilestoneDot: React.FC<{ pt: Pt; at: number; drawn: any; color: string; r: number }> = ({ pt, at, drawn, color, r }) => {
  const scale = useTransform(drawn, [at - 5, at + 25], [0, 1]);
  return (
    <motion.g style={{ scale, transformOrigin: `${pt[0]}px ${pt[1]}px` }}>
      <circle cx={pt[0]} cy={pt[1]} r={r + 6} fill="#fff" />
      <circle cx={pt[0]} cy={pt[1]} r={r} fill={color} />
    </motion.g>
  );
};

const MilestoneLabel: React.FC<{ pt: Pt; at: number; drawn: any; label: string; date: string; W: number; H: number }> = ({ pt, at, drawn, label, date, W, H }) => {
  const opacity = useTransform(drawn, [at + 10, at + 60], [0, 1]);
  return (
    <Tooltip title={date} arrow placement="top">
      <Box component={motion.div} style={{ opacity, position: 'absolute', left: pct(pt[0] - 90, W), top: pct(pt[1] - 60, H), width: pct(180, W) }}
        sx={{ textAlign: 'center', cursor: 'default' }}>
        <Typography variant="caption" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{label}</Typography>
      </Box>
    </Tooltip>
  );
};

export default TwoLines;
